import type { CmmdTaskWorkflow } from "@cmmd-forge/contracts";
import { ArrowDownIcon, ArrowRightIcon, ArrowUpIcon, PlusIcon, Trash2Icon } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import { Input } from "~/components/ui/input";
import { ProjectTaskWorkflowStageSelect } from "./ProjectTaskWorkflowStageSelect";
import { replaceWorkflowTransitionIfValid } from "./taskWorkflowTransitions";

type WorkflowStage = CmmdTaskWorkflow["stages"][number];
type WorkflowCategory = WorkflowStage["category"];

const CATEGORY_LABELS: ReadonlyArray<{ readonly value: WorkflowCategory; readonly label: string }> =
  [
    { value: "backlog", label: "Backlog" },
    { value: "todo", label: "Todo" },
    { value: "in_progress", label: "In progress" },
    { value: "in_review", label: "In review" },
    { value: "done", label: "Done" },
  ];
const MAX_WORKFLOW_STAGES = 20;
const MAX_WORKFLOW_TRANSITIONS = 100;

function normalizePositions(workflow: CmmdTaskWorkflow): CmmdTaskWorkflow {
  return {
    ...workflow,
    stages: workflow.stages.map((stage, position) => ({ ...stage, position })),
  };
}

export interface ProjectTaskWorkflowEditorProps {
  readonly draft: CmmdTaskWorkflow;
  readonly disabled: boolean;
  readonly onChange: (draft: CmmdTaskWorkflow) => void;
}

export function ProjectTaskWorkflowEditor(props: ProjectTaskWorkflowEditorProps) {
  const { draft, disabled, onChange } = props;
  const moveStage = (index: number, offset: -1 | 1) => {
    const target = index + offset;
    if (target < 0 || target >= draft.stages.length) return;
    const stages = [...draft.stages];
    const currentStage = stages[index];
    const targetStage = stages[target];
    if (!currentStage || !targetStage) return;
    stages[index] = targetStage;
    stages[target] = currentStage;
    onChange(normalizePositions({ ...draft, stages }));
  };
  const updateStage = (id: string, patch: Partial<WorkflowStage>) => {
    onChange({
      ...draft,
      stages: draft.stages.map((stage) => (stage.id === id ? { ...stage, ...patch } : stage)),
      transitions:
        patch.category === "done"
          ? draft.transitions.map((edge) =>
              edge.toStageId === id ? { ...edge, requiresHuman: true } : edge,
            )
          : draft.transitions,
    });
  };
  const removeStage = (id: string) => {
    const stages = draft.stages.filter((stage) => stage.id !== id);
    const transitions = draft.transitions.filter(
      (edge) => edge.fromStageId !== id && edge.toStageId !== id,
    );
    if (stages.length < 2 || transitions.length < 1) return;
    onChange(normalizePositions({ ...draft, stages, transitions }));
  };
  const addStage = () => {
    const id = crypto.randomUUID();
    onChange({
      ...draft,
      stages: [
        ...draft.stages,
        {
          id,
          key: `custom_${id.replaceAll("-", "").slice(0, 10)}`,
          name: "New stage",
          category: "in_progress",
          color: "#64748b",
          position: draft.stages.length,
          isRequired: false,
        },
      ],
    });
  };
  const addTransition = () => {
    const existing = new Set(
      draft.transitions.map((edge) => `${edge.fromStageId}:${edge.toStageId}`),
    );
    const candidate = draft.stages
      .flatMap((from) => draft.stages.map((to) => ({ from, to })))
      .find(({ from, to }) => from.id !== to.id && !existing.has(`${from.id}:${to.id}`));
    if (!candidate) return;
    onChange({
      ...draft,
      transitions: [
        ...draft.transitions,
        {
          fromStageId: candidate.from.id,
          toStageId: candidate.to.id,
          requiresHuman: candidate.to.category === "done",
        },
      ],
    });
  };
  return (
    <fieldset disabled={disabled} className="space-y-8 disabled:opacity-70">
      <section>
        <h3 className="text-sm font-semibold">Stages</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Rename and reorder the stages people see. Categories keep reporting stable.
        </p>
        <div className="mt-4 space-y-2" data-testid="forge-workflow-stages">
          {draft.stages.map((stage, index) => (
            <div
              className="grid min-h-10 grid-cols-[auto_1fr] gap-2 rounded-lg px-1 py-1 hover:bg-muted/50 sm:flex sm:items-center"
              key={stage.id}
            >
              <input
                aria-label={`${stage.name} color`}
                className="size-8 bg-transparent sm:size-5"
                type="color"
                value={stage.color.startsWith("#") ? stage.color : "#64748b"}
                onChange={(event) => updateStage(stage.id, { color: event.target.value })}
              />
              <Input
                aria-label="Stage name"
                maxLength={80}
                className="h-8 min-w-0 sm:flex-1"
                value={stage.name}
                onChange={(event) => updateStage(stage.id, { name: event.target.value })}
              />
              <select
                aria-label={`${stage.name} category`}
                disabled={disabled || stage.isRequired}
                className="col-span-2 h-8 min-w-0 rounded-md border border-input bg-background px-2 text-xs disabled:opacity-60 sm:col-span-1 sm:w-32"
                value={stage.category}
                onChange={(event) =>
                  updateStage(stage.id, { category: event.target.value as WorkflowCategory })
                }
              >
                {CATEGORY_LABELS.map((category) => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
              <div className="col-span-2 flex justify-end gap-1 sm:contents">
                <Button
                  aria-label={`Move ${stage.name} up`}
                  disabled={disabled || index === 0}
                  onClick={() => moveStage(index, -1)}
                  size="icon-sm"
                  variant="ghost"
                >
                  <ArrowUpIcon />
                </Button>
                <Button
                  aria-label={`Move ${stage.name} down`}
                  disabled={disabled || index === draft.stages.length - 1}
                  onClick={() => moveStage(index, 1)}
                  size="icon-sm"
                  variant="ghost"
                >
                  <ArrowDownIcon />
                </Button>
                <Button
                  aria-label={`Remove ${stage.name}`}
                  disabled={
                    disabled ||
                    stage.isRequired ||
                    draft.stages.length <= 2 ||
                    draft.transitions.filter(
                      (edge) => edge.fromStageId !== stage.id && edge.toStageId !== stage.id,
                    ).length === 0
                  }
                  onClick={() => removeStage(stage.id)}
                  size="icon-sm"
                  variant="ghost"
                >
                  <Trash2Icon />
                </Button>
              </div>
            </div>
          ))}
        </div>
        <Button
          className="mt-3"
          disabled={disabled || draft.stages.length >= MAX_WORKFLOW_STAGES}
          onClick={addStage}
          size="sm"
          variant="outline"
        >
          <PlusIcon />
          Add stage
        </Button>
      </section>
      <section className="border-t border-border pt-7">
        <h3 className="text-sm font-semibold">Allowed transitions</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          A human-only rule prevents agents and automations from approving their own work.
        </p>
        <div className="mt-4 space-y-2" data-testid="forge-workflow-transitions">
          {draft.transitions.map((edge, index) => {
            const targetStage = draft.stages.find((stage) => stage.id === edge.toStageId);
            const isHumanApprovalGate = targetStage?.category === "done";
            return (
              <div
                className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 rounded-lg border border-border p-2 sm:flex sm:border-0 sm:p-0"
                key={`${edge.fromStageId}:${edge.toStageId}`}
              >
                <ProjectTaskWorkflowStageSelect
                  ariaLabel="From stage"
                  stages={draft.stages}
                  value={edge.fromStageId}
                  onChange={(fromStageId) => {
                    const next = replaceWorkflowTransitionIfValid(draft, index, { fromStageId });
                    if (next != null) onChange(next);
                  }}
                />
                <ArrowRightIcon className="size-4 text-muted-foreground" />
                <ProjectTaskWorkflowStageSelect
                  ariaLabel="To stage"
                  stages={draft.stages}
                  value={edge.toStageId}
                  onChange={(toStageId) => {
                    const next = replaceWorkflowTransitionIfValid(draft, index, {
                      toStageId,
                      requiresHuman:
                        draft.stages.find((stage) => stage.id === toStageId)?.category === "done"
                          ? true
                          : edge.requiresHuman,
                    });
                    if (next != null) onChange(next);
                  }}
                />
                <label className="col-span-3 flex items-center gap-2 text-xs text-muted-foreground sm:w-36">
                  <Checkbox
                    disabled={disabled || isHumanApprovalGate}
                    checked={isHumanApprovalGate || edge.requiresHuman}
                    onCheckedChange={(requiresHuman) =>
                      onChange({
                        ...draft,
                        transitions: draft.transitions.map((entry, entryIndex) =>
                          entryIndex === index
                            ? { ...entry, requiresHuman: requiresHuman === true }
                            : entry,
                        ),
                      })
                    }
                  />
                  Human approval
                </label>
                <Button
                  className="col-span-3 justify-self-end"
                  aria-label="Remove transition"
                  disabled={disabled || draft.transitions.length === 1}
                  onClick={() =>
                    onChange({
                      ...draft,
                      transitions: draft.transitions.filter(
                        (_, entryIndex) => entryIndex !== index,
                      ),
                    })
                  }
                  size="icon-sm"
                  variant="ghost"
                >
                  <Trash2Icon />
                </Button>
              </div>
            );
          })}
        </div>
        <Button
          className="mt-3"
          disabled={disabled || draft.transitions.length >= MAX_WORKFLOW_TRANSITIONS}
          onClick={addTransition}
          size="sm"
          variant="outline"
        >
          <PlusIcon />
          Add transition
        </Button>
      </section>
    </fieldset>
  );
}
